import { z as e } from "zod";
const Me = e.object({
  name: e.string(),
  id: e.string(),
  email: e.string(),
  role: e.string(),
  created_at: e.date(),
  deleted_at: e.date().nullable()
}), xe = "editorTextBox", $ = e.object({
  orderId: e.string(),
  orderProductId: e.string(),
  applicationType: e.string(),
  userId: e.string(),
  funeralHomeId: e.string(),
  timestamp: e.string()
}), ze = e.discriminatedUnion("type", [
  e.object({
    type: e.literal("EDITOR_BACK_BUTTON"),
    payload: $
  })
]), Be = e.object({
  orderId: e.string(),
  orderProductId: e.string(),
  applicationType: e.string()
}), Ge = e.object({
  success: e.boolean(),
  message: e.string(),
  action: e.enum(["created", "existing", "processing", "skipped", "notified"]),
  pdfId: e.string().optional()
}), He = {
  MDL_EDITOR_EVENTS: "mdl_editor_events"
}, s = (o) => e.guid({
  // The message names a malformed id only: a missing value, or one that is
  // not a string, keeps zod's own message, which says what was received.
  error: (n) => n.code === "invalid_format" ? o : void 0
}), t = s(), we = e.object({
  fromDate: e.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
    error: "Invalid date format. Expected format: yyyy-mm-dd"
  }).optional()
}), ke = e.object({
  pageSetId: t,
  themeId: t,
  productId: t,
  applyToTheme: t.optional(),
  funeralHomeId: t.optional()
}), Ve = e.object({
  sourceProductId: t,
  targetProductId: t,
  sourceThemeId: t.optional(),
  sourcePageSetIds: e.array(t).optional(),
  funeralHomeId: t.optional()
}).refine((o) => o.sourceProductId !== o.targetProductId, {
  path: ["targetProductId"],
  error: "Cannot copy to the same product"
}), qe = e.object({
  productName: e.string().min(1),
  foldLine: e.enum(["vertical-center", "horizontal-center", "none"]),
  numberOfPages: e.number().min(1),
  thumbnailURL: e.url(),
  widthInches: e.number().min(1),
  heightInches: e.number().min(1),
  widthPixels: e.number().min(1),
  heightPixels: e.number().min(1),
  padding: e.object({
    top: e.number().min(0),
    right: e.number().min(0),
    bottom: e.number().min(0),
    left: e.number().min(0),
    centerHorizontal: e.number().min(0),
    centerVertical: e.number().min(0)
  })
}), Qe = e.object({
  productId: t,
  application: e.enum(["MDL", "M3"]),
  themeId: t.optional(),
  numberOfPages: e.int().min(1).optional()
}), We = e.object({
  baseThemeId: t,
  newThemeName: e.string().min(1)
}), R = 16383, Z = {
  prefix: "prefix",
  first: "first",
  nickname: "nickname",
  middle: "middle",
  last: "last",
  suffix: "suffix",
  maiden: "maiden"
}, U = ["M3", "MDL"], K = {
  // The AI image edits switch (Site Settings > AI Image Edits in M3) is off,
  // so no background removal or Enhance may start or be accepted.
  AI_IMAGE_EDITS_DISABLED: "AI_IMAGE_EDITS_DISABLED"
}, X = {
  QUEUED: "QUEUED",
  PROCESSING: "PROCESSING",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED"
}, m = {
  BG_REMOVAL: "BG_REMOVAL",
  UPSCALE: "UPSCALE"
}, c = {
  DAILY_CAP_REACHED: "DAILY_CAP_REACHED",
  MODERATION_BLOCKED: "MODERATION_BLOCKED",
  SPEND_LIMIT: "SPEND_LIMIT",
  NOT_CONFIGURED: "NOT_CONFIGURED",
  MODEL_ERROR: "MODEL_ERROR",
  ENQUEUE_FAILED: "ENQUEUE_FAILED",
  ASPECT_UNSUPPORTED: "ASPECT_UNSUPPORTED",
  AI_IMAGE_EDITS_DISABLED: K.AI_IMAGE_EDITS_DISABLED
};
new Set(
  Object.values(c)
);
const Y = {
  EDITING: "EDITING",
  PROOF_SENT: "PROOF_SENT",
  FINALIZED: "FINALIZED",
  CLOSED: "CLOSED"
}, a = {
  EN: "EN",
  ES: "ES",
  FR: "FR"
};
Object.values(a);
const A = {
  "Standard Theme Design": {
    prompt: "imagePlacement",
    steps: ({ imagePlacementChoice: o }) => o === "self" ? ["placement", "review"] : ["review"]
  },
  // The designer invents the background, so the theme selector is replaced by
  // the custom-theme brief; Continue goes straight to the review modal.
  "Custom Theme Design": {
    prompt: "customTheme",
    steps: () => ["review"]
  },
  // The order-of-service content rides in as a document upload or typed
  // text below the case dropdown; Continue goes straight to review.
  "Order of Service Partner": {
    prompt: "orderOfService",
    steps: () => ["review"]
  },
  // The requested layout adjustments ride in as free text below the case
  // dropdown; Continue goes straight to review.
  "Layout Partner": {
    prompt: "layoutChanges",
    steps: () => ["review"]
  },
  // Photo selections + edit comments are normally collected on the product
  // page; the modal step only runs when the customer hits Continue without
  // finishing them.
  "Photograph Editor": {
    prompt: "photoEdits",
    steps: ({ photoEditsComplete: o }) => o ? ["review"] : ["photoEdits", "review"]
  },
  // The extra photos (uploaded through the case-image pipeline or picked
  // from existing case images) are normally collected on the product page;
  // the modal step only runs when Continue is hit without any added.
  "Additional Photos": {
    prompt: "additionalPhotos",
    steps: ({ additionalPhotosComplete: o }) => o ? ["review"] : ["additionalPhotos", "review"]
  }
}, J = "Signature by Beryl", ee = {
  [J]: "Standard Theme Design",
  "Design my product using a standard theme": "Standard Theme Design",
  "Design my product using a custom theme": "Custom Theme Design",
  "Add an order of service": "Order of Service Partner",
  "Design my layouts": "Layout Partner",
  "Edit my photos": "Photograph Editor",
  "Add more photos": "Additional Photos"
};
new Map([
  ...Object.entries(A).map(
    ([o, n]) => [o.toLowerCase(), n]
  ),
  ...Object.entries(ee).map(
    ([o, n]) => [o.toLowerCase(), A[n]]
  )
]);
const te = [
  "unassigned",
  "assigned",
  "approved",
  "completed",
  "canceled"
], oe = (o) => o.trim().replace(/^['"]|['"]$/g, ""), ne = (o) => oe(o.split(",")[0] ?? ""), ae = /^[A-Za-z0-9_-]*$/, r = e.enum(U).optional().default("MDL"), $e = e.object({
  categoryId: t.optional(),
  getAllAssets: e.boolean().optional()
}), Ze = e.object({
  categoryId: e.string().trim().pipe(t),
  name: e.string().trim()
}), Ke = e.object({
  categoryId: e.string().trim().pipe(t).optional(),
  name: e.string().trim().optional()
}), ie = e.object({
  subcategoryId: t.optional(),
  name: e.string().trim().optional()
}), Xe = e.object({
  subcategoryId: t,
  name: e.string().trim(),
  image: e.string().trim()
}), Ye = ie.extend({
  image: e.string().trim().optional()
}), Je = e.object({
  productId: t.optional(),
  themeId: t.optional()
}), et = e.object({
  productId: t,
  themeId: t,
  fontFamily: e.string(),
  textColor: e.string()
}), tt = e.object({
  productId: t.optional().nullable(),
  themeId: t.optional().nullable(),
  fontFamily: e.string().optional().nullable(),
  textColor: e.string().optional().nullable()
}), re = 25, N = Object.values(Z), _ = e.enum(N), L = (o) => new Set(o).size === o.length, F = e.strictObject({
  order: e.array(_).length(N.length).refine(L, {
    error: "order must hold every part once"
  }),
  hidden: e.array(_).refine(L, {
    error: "hidden must list a part once"
  })
}), h = (o) => Array.isArray(o) ? o.filter(
  (n) => n && typeof n == "object" && "name" in n && typeof n.name == "string" && n.name.trim() !== ""
) : o, ot = e.object({
  limit: e.coerce.number().int().min(1).max(50).optional().default(re),
  page: e.coerce.number().int().min(1).optional().default(1),
  archived: e.coerce.boolean().default(!1),
  searchTerm: e.string().optional(),
  recentOnly: e.coerce.boolean().default(!0)
}), I = e.array(
  e.object({
    id: e.string().optional().nullable(),
    location: e.string().trim().nullable().optional(),
    time: e.string().trim().nullable().optional(),
    date: e.string().nullable().optional(),
    type: e.string().trim().nonempty(),
    default: e.boolean().default(!1),
    city: e.string().trim().nullable().optional(),
    state: e.string().trim().nullable().optional(),
    address: e.string().trim().nullable().optional(),
    clergyPlace: e.string().trim().nullable().optional(),
    country: e.string().trim().nullable().optional(),
    endTime: e.string().trim().nullable().optional(),
    postalCode: e.string().trim().nullable().optional(),
    roomChapel: e.string().trim().nullable().optional(),
    startTime: e.string().trim().nullable().optional()
  })
).nullable().optional(), le = (o) => Array.isArray(o) ? o.filter(
  (n) => !(n !== null && typeof n == "object" && "name" in n && typeof n.name == "string" && n.name.trim() === "")
) : o, f = e.union([
  e.preprocess(
    le,
    e.array(
      e.object({
        name: e.string().trim().min(1, {
          error: "Name cannot be empty or just spaces"
        }),
        relation: e.string().trim().nullable().optional(),
        type: e.string().trim().min(1, {
          error: "Type cannot be empty or just spaces"
        }),
        city: e.string().trim().nullable().optional(),
        country: e.string().trim().nullable().optional(),
        state: e.string().trim().nullable().optional(),
        id: e.string().optional().nullable()
      })
    ).optional()
  ),
  e.string().trim().optional()
]).nullable().optional(), S = e.preprocess(
  h,
  e.array(
    e.object({
      name: e.string().trim().nonempty(),
      sequence: e.union([e.string().trim().nonempty(), e.number()]),
      charityOfChoice: e.boolean(),
      id: e.string().optional().nullable()
    })
  ).nullable().optional()
), y = e.preprocess(
  h,
  e.array(
    e.object({
      name: e.string().trim().nonempty(),
      sequence: e.union([e.string().trim(), e.number()]).nullable().optional(),
      type: e.string().trim().nonempty(),
      id: e.string().optional().nullable()
    })
  ).nullable().optional()
), T = e.preprocess(
  h,
  e.array(
    e.object({
      name: e.string().trim().nonempty(),
      sequence: e.union([e.string().trim().nonempty(), e.number()]),
      city: e.string().trim().nullable().optional(),
      state: e.string().trim().nullable().optional(),
      zipCode: e.string().trim().nullable().optional(),
      country: e.string().trim().nullable().optional(),
      websiteUrl: e.string().trim().nullable().optional(),
      address1: e.string().trim().nullable().optional(),
      address2: e.string().trim().nullable().optional(),
      id: e.string().optional().nullable()
    })
  ).nullable().optional()
), nt = e.object({
  birth_header: e.string().optional(),
  death_header: e.string().optional(),
  remembrance_header: e.string().optional(),
  officiating_header: e.string().optional(),
  memorial_service_header: e.string().optional(),
  final_resting_header: e.string().optional(),
  pallbearers_header: e.string().optional(),
  visitation_header: e.string().optional(),
  family_appreciation_header: e.string().optional()
}), O = e.object({
  birthHeader: e.string().trim().optional(),
  deathHeader: e.string().trim().optional(),
  remembranceHeader: e.string().trim().optional(),
  officiatingHeader: e.string().trim().optional(),
  memorialServiceHeader: e.string().trim().optional(),
  finalRestingHeader: e.string().trim().optional(),
  pallbearersHeader: e.string().trim().optional(),
  visitationHeader: e.string().trim().optional(),
  familyAppreciationHeader: e.string().trim().optional()
}).nullable().optional(), se = 2048, me = /^https?:\/\//i, ce = /^[a-z0-9-]+(\.[a-z0-9-]+)+(:\d+)?([/?#]|$)/i, pe = (o) => {
  if (o === "") return !0;
  const n = me.test(o) ? o : ce.test(o) ? `https://${o}` : null;
  if (!n) return !1;
  try {
    const i = new URL(n);
    return (i.protocol === "http:" || i.protocol === "https:") && !!i.hostname;
  } catch {
    return !1;
  }
}, M = e.string().max(se).refine(pe, {
  error: "returnUrl must be an http(s) URL or a host name without a scheme"
}).nullable().optional(), ue = e.object({
  nameOfDeceased: e.coerce.string().trim().nullable().optional(),
  nameArrangement: F.optional(),
  editableCaseId: e.string().trim().nullable().optional(),
  dateOfBirth: e.string().nullable().optional(),
  placeOfBirth: e.string().trim().nullable().optional(),
  dateOfDeath: e.string().nullable().optional(),
  placeOfDeath: e.string().trim().nullable().optional(),
  motherOfDeceased: e.string().trim().nullable().optional(),
  fatherOfDeceased: e.string().trim().nullable().optional(),
  spouse: e.string().trim().nullable().optional(),
  dateOfMarriage: e.coerce.date().nullable().optional(),
  maternalGrandmother: e.string().trim().nullable().optional(),
  maternalGrandmothersMother: e.string().trim().nullable().optional(),
  maternalGrandmothersFather: e.string().trim().nullable().optional(),
  maternalGrandfather: e.string().trim().nullable().optional(),
  maternalGrandfathersMother: e.string().trim().nullable().optional(),
  maternalGrandfathersFather: e.string().trim().nullable().optional(),
  paternalGrandmother: e.string().trim().nullable().optional(),
  paternalGrandmothersMother: e.string().trim().nullable().optional(),
  paternalGrandmothersFather: e.string().trim().nullable().optional(),
  paternalGrandfather: e.string().trim().nullable().optional(),
  paternalGrandfathersMother: e.string().trim().nullable().optional(),
  paternalGrandfathersFather: e.string().trim().nullable().optional(),
  children: e.array(e.string()).default([]),
  siblings: e.array(e.string()).default([]),
  organizations: T,
  songs: e.array(e.string()).default([]),
  pallbearers: y,
  obituary: e.string().trim().nullable().optional(),
  familyAppreciationMessage: e.string().trim().nullable().optional(),
  services: I,
  additionalFamily: f,
  clergy: e.array(e.string()).default([]),
  intermentPlace: e.string().trim().nullable().optional(),
  intermentLot: e.string().trim().nullable().optional(),
  intermentSection: e.string().trim().nullable().optional(),
  intermentBlock: e.string().trim().nullable().optional(),
  intermentCity: e.string().trim().nullable().optional(),
  intermentState: e.string().trim().nullable().optional(),
  intermentDate: e.string().nullable().optional(),
  intermentCounty: e.string().trim().nullable().optional(),
  intermentTime: e.string().trim().nullable().optional(),
  createdOnBehalfOfEmail: e.string().trim().optional(),
  intermentAddress: e.string().trim().optional(),
  lastdec: e.union([e.string(), e.boolean()]).transform((o) => typeof o == "boolean" ? o ? "True" : "False" : o.trim()).nullable().optional(),
  firstDec: e.union([e.string(), e.boolean()]).transform((o) => typeof o == "boolean" ? o ? "True" : "False" : o.trim()).nullable().optional(),
  maidenDec: e.union([e.string(), e.boolean()]).transform((o) => typeof o == "boolean" ? o ? "True" : "False" : o.trim()).nullable().optional(),
  middleDec: e.union([e.string(), e.boolean()]).transform((o) => typeof o == "boolean" ? o ? "True" : "False" : o.trim()).nullable().optional(),
  nickNameDec: e.union([e.string(), e.boolean()]).transform((o) => typeof o == "boolean" ? o ? "True" : "False" : o.trim()).nullable().optional(),
  prefixDec: e.union([e.string(), e.boolean()]).transform((o) => typeof o == "boolean" ? o ? "True" : "False" : o.trim()).nullable().optional(),
  suffixDec: e.union([e.string(), e.boolean()]).transform((o) => typeof o == "boolean" ? o ? "True" : "False" : o.trim()).nullable().optional(),
  websiteObitUrl: e.string().trim().optional(),
  orderOfService: e.string().trim().optional(),
  memorialContributions: S,
  returnUrl: M,
  intermentPostalCode: e.string().trim().optional(),
  casePreferences: O
}), at = e.object({
  nameOfDeceased: e.coerce.string().trim().nullable().optional(),
  nameArrangement: F.optional(),
  editableCaseId: e.string().trim().nullable().optional(),
  dateOfBirth: e.string().nullable().optional(),
  placeOfBirth: e.string().trim().nullable().optional(),
  dateOfDeath: e.string().nullable().optional(),
  placeOfDeath: e.string().trim().nullable().optional(),
  motherOfDeceased: e.string().trim().nullable().optional(),
  fatherOfDeceased: e.string().trim().nullable().optional(),
  spouse: e.string().trim().nullable().optional(),
  dateOfMarriage: e.coerce.date().nullable().optional(),
  maternalGrandmother: e.string().trim().nullable().optional(),
  maternalGrandmothersMother: e.string().trim().nullable().optional(),
  maternalGrandmothersFather: e.string().trim().nullable().optional(),
  maternalGrandfather: e.string().trim().nullable().optional(),
  maternalGrandfathersMother: e.string().trim().nullable().optional(),
  maternalGrandfathersFather: e.string().trim().nullable().optional(),
  paternalGrandmother: e.string().trim().nullable().optional(),
  paternalGrandmothersMother: e.string().trim().nullable().optional(),
  paternalGrandmothersFather: e.string().trim().nullable().optional(),
  paternalGrandfather: e.string().trim().nullable().optional(),
  paternalGrandfathersMother: e.string().trim().nullable().optional(),
  paternalGrandfathersFather: e.string().trim().nullable().optional(),
  children: e.array(e.string()).optional(),
  siblings: e.array(e.string()).optional(),
  organizations: T,
  songs: e.array(e.string()).optional(),
  pallbearers: y,
  obituary: e.string().trim().nullable().optional(),
  familyAppreciationMessage: e.string().trim().nullable().optional(),
  services: I,
  additionalFamily: f,
  clergy: e.array(e.string()).optional(),
  intermentPlace: e.string().trim().nullable().optional(),
  intermentLot: e.string().trim().nullable().optional(),
  intermentSection: e.string().trim().nullable().optional(),
  intermentBlock: e.string().trim().nullable().optional(),
  intermentCity: e.string().trim().nullable().optional(),
  intermentState: e.string().trim().nullable().optional(),
  intermentDate: e.string().nullable().optional(),
  intermentCounty: e.string().trim().nullable().optional(),
  intermentTime: e.string().trim().nullable().optional(),
  createdOnBehalfOfEmail: e.string().trim().optional(),
  intermentAddress: e.string().trim().optional(),
  lastdec: e.union([e.string(), e.boolean()]).transform((o) => typeof o == "boolean" ? o ? "True" : "False" : o.trim()).nullable().optional(),
  firstDec: e.union([e.string(), e.boolean()]).transform((o) => typeof o == "boolean" ? o ? "True" : "False" : o.trim()).nullable().optional(),
  maidenDec: e.union([e.string(), e.boolean()]).transform((o) => typeof o == "boolean" ? o ? "True" : "False" : o.trim()).nullable().optional(),
  middleDec: e.union([e.string(), e.boolean()]).transform((o) => typeof o == "boolean" ? o ? "True" : "False" : o.trim()).nullable().optional(),
  nickNameDec: e.union([e.string(), e.boolean()]).transform((o) => typeof o == "boolean" ? o ? "True" : "False" : o.trim()).nullable().optional(),
  prefixDec: e.union([e.string(), e.boolean()]).transform((o) => typeof o == "boolean" ? o ? "True" : "False" : o.trim()).nullable().optional(),
  suffixDec: e.union([e.string(), e.boolean()]).transform((o) => typeof o == "boolean" ? o ? "True" : "False" : o.trim()).nullable().optional(),
  websiteObitUrl: e.string().trim().optional(),
  orderOfService: e.string().trim().optional(),
  memorialContributions: S,
  returnUrl: M,
  intermentPostalCode: e.string().trim().optional(),
  casePreferences: O
}), it = e.object({
  services: I,
  additionalFamily: f,
  memorialContributions: S,
  pallbearers: y,
  organizations: T,
  casePreferences: O
}), rt = e.object({
  caseId: t,
  contentType: e.string().regex(/^image\//, "contentType must be an image MIME type")
}), lt = e.object({
  caseId: t,
  s3Key: e.string().min(1),
  originalFileName: e.string().optional()
}), st = e.object({
  caseId: t,
  imageUrl: e.url()
}), mt = e.object({
  caseId: t,
  fromIndex: e.int().nonnegative()
}), ct = e.object({
  caseId: t,
  contentTypes: e.array(
    e.string().regex(/^image\//, "contentType must be an image MIME type")
  ).min(1).max(20)
}), pt = e.object({
  caseId: t,
  items: e.array(
    e.object({
      s3Key: e.string().min(1),
      originalFileName: e.string().optional()
    })
  ).min(1).max(20)
}), ut = e.object({
  jobIds: e.array(e.string().min(1)).min(1).max(20)
}), dt = e.object({
  token: e.string().min(1),
  contentType: e.string().regex(/^image\//, "contentType must be an image MIME type")
}), gt = e.object({
  token: e.string().min(1),
  s3Key: e.string().min(1),
  originalFileName: e.string().optional()
}), bt = e.object({
  token: e.string().min(1),
  imageUrl: e.url()
}), ht = e.object({
  token: e.string().min(1)
}), It = e.object({
  token: e.string().min(1),
  imageUrl: e.url()
}), ft = e.object({
  token: e.string().min(1)
}), b = `targetSize edges must be whole pixels from 1 to ${R}`, de = "targetSize is only accepted with kind UPSCALE", v = e.int({ error: b }).min(1, { error: b }).max(R, {
  error: b
}), ge = e.object({
  width: v,
  height: v
}), St = e.object({
  caseImageId: t,
  kind: e.enum(m),
  sourceVariantId: t.optional(),
  targetSize: ge.optional()
}).refine(
  (o) => o.targetSize === void 0 || o.kind === m.UPSCALE,
  { error: de, path: ["targetSize"] }
), yt = e.object({
  code: e.enum([
    c.DAILY_CAP_REACHED,
    c.ASPECT_UNSUPPORTED
  ])
}), x = e.object({
  id: t,
  kind: e.enum(m),
  status: e.enum(X),
  url: e.string().nullable(),
  sourceVariantId: t.nullable(),
  accepted: e.boolean(),
  revertedAt: e.iso.datetime().nullable(),
  errorCode: e.enum(c).nullable(),
  createdAt: e.iso.datetime()
}), Tt = e.object({
  variants: e.array(x)
}), be = x.extend({
  caseImageId: t
}), Ot = e.object({
  variants: e.array(be)
}), jt = e.object({
  reverted: e.boolean()
}), Pt = e.object({
  variantId: t,
  revertedAt: e.iso.datetime().nullable()
}), Et = e.object({
  [m.BG_REMOVAL]: e.int().nonnegative(),
  [m.UPSCALE]: e.int().nonnegative()
}), Ct = e.object({
  enabled: e.boolean()
}), Dt = e.object({
  caseId: e.string(),
  archived: e.coerce.boolean().default(!1)
}), At = e.object({
  memorial: e.string().trim(),
  submitterName: e.string().trim(),
  caseId: e.string()
}), _t = e.object({
  htmlForRender: e.string().trim(),
  html: e.string().trim(),
  reference: e.string().trim(),
  isGlobal: e.boolean().optional().nullable(),
  imageUrl: e.string().optional().nullable(),
  location: e.string().optional().nullable(),
  type: e.enum(["HTML", "IMAGE"]).default("HTML"),
  themeId: t.optional().nullable(),
  baseProductId: t.optional().nullable(),
  orderProductId: t.optional().nullable()
}), Lt = e.object({
  themeId: t.optional().nullable(),
  baseProductId: t.optional().nullable(),
  orderProductId: t.optional().nullable()
}), z = e.enum(["button", "thumbnail"]), j = e.enum([
  "base",
  "enhancement",
  "theme",
  "template",
  "dynamic_content"
]), B = e.enum([
  "all",
  "specific",
  "all_plus_specific",
  "all_except"
]), he = e.object({
  customizationLabel: e.string(),
  optionLabels: e.array(e.string())
}), p = e.object({
  label: e.string().trim().min(1),
  image: e.string().optional(),
  iconAssetUrl: e.url().optional(),
  sortOrder: e.int().optional(),
  available: e.boolean().optional(),
  description: e.string().optional(),
  themeId: t.optional(),
  dependsOn: e.array(he).optional()
}), vt = p.partial(), Ie = e.strictObject({
  productId: t,
  label: e.string().trim().min(1),
  displayType: z.default("button"),
  customizationType: j.default("base"),
  setDefault: e.boolean().optional(),
  defaultOption: t.optional(),
  available: e.boolean().default(!0),
  sortOrder: e.int().min(0).optional(),
  themeMode: B.optional(),
  options: e.array(p).optional()
}), Rt = Ie.refine(
  (o) => !(o.setDefault === !0 && !o.defaultOption),
  {
    path: ["defaultOption"],
    error: "defaultOption is required when setDefault is true"
  }
).refine(
  (o) => {
    if (o.defaultOption)
      try {
        return t.parse(o.defaultOption), !0;
      } catch {
        return !1;
      }
    return !0;
  },
  {
    path: ["defaultOption"],
    error: "defaultOption must be a valid UUID"
  }
).refine(
  (o) => {
    if (o.options && o.options.length > 0) {
      const n = o.options.map((l) => l.label.toLowerCase()), i = new Set(n);
      return n.length === i.size;
    }
    return !0;
  },
  {
    path: ["options"],
    error: "Option labels must be unique (case-insensitive)"
  }
), fe = e.strictObject({
  productId: t.optional(),
  label: e.string().trim().min(1).optional(),
  displayType: z.optional(),
  customizationType: j.optional(),
  setDefault: e.boolean().optional(),
  defaultOption: t.optional(),
  available: e.boolean().optional(),
  sortOrder: e.int().min(0).optional(),
  themeMode: B.nullable().optional(),
  options: e.array(p).optional(),
  archived: e.boolean().optional()
}), Ut = fe.refine(
  (o) => !(o.setDefault === !0 && !o.defaultOption),
  {
    path: ["defaultOption"],
    error: "defaultOption is required when setDefault is true"
  }
).refine(
  (o) => {
    if (o.defaultOption)
      try {
        return t.parse(o.defaultOption), !0;
      } catch {
        return !1;
      }
    return !0;
  },
  {
    path: ["defaultOption"],
    error: "defaultOption must be a valid UUID"
  }
).refine(
  (o) => {
    if (o.options && o.options.length > 0) {
      const n = o.options.map((l) => l.label.toLowerCase()), i = new Set(n);
      return n.length === i.size;
    }
    return !0;
  },
  {
    path: ["options"],
    error: "Option labels must be unique (case-insensitive)"
  }
), Nt = e.object({
  productId: t.optional(),
  archived: e.boolean().optional(),
  available: e.boolean().optional(),
  customizationType: j.optional()
}), Ft = e.object({
  id: t
}), Mt = e.object({
  id: t
}), xt = e.object({
  options: e.array(p).min(1)
}), P = e.object({
  customizationId: t,
  selectedOptionId: t
}), E = e.record(e.string(), e.string()), zt = e.object({
  bucket: e.enum(te).optional()
}), Bt = e.object({
  id: t
}), Gt = e.object({
  themeOverrideId: e.string(),
  archived: e.coerce.boolean().default(!1)
}), Ht = (o) => typeof o == "object" && o !== null && o.name === "ZodError" && Array.isArray(o.issues), Se = (o, n) => {
  o.issues.push({
    code: "custom",
    input: o.value,
    continue: !0,
    ...n
  });
}, ye = e.strictObject({
  pageId: t,
  content: e.string().min(1),
  loadedRunId: e.string().min(1).max(128).nullable(),
  boxes: e.array(e.unknown()),
  deleteBoxIds: e.array(e.string().min(1)).default([])
}), wt = e.strictObject({
  applicationType: e.enum(U),
  coveringVersionNumber: e.int().positive(),
  runId: e.string().min(1).max(128),
  pages: e.array(ye).min(1)
}).check((o) => {
  const { value: n } = o, i = /* @__PURE__ */ new Set();
  for (const [l, g] of n.pages.entries())
    i.has(g.pageId) && Se(o, {
      path: ["pages", l, "pageId"],
      message: `page "${g.pageId}" appears twice — one entry per page`
    }), i.add(g.pageId);
}), Te = {
  maxRetries: 2,
  maxWaitMs: 1e4,
  timeoutMs: 12e4
}, kt = () => {
  const { maxRetries: o, maxWaitMs: n, timeoutMs: i } = Te;
  return o * (n + i);
}, G = e.enum(["button", "thumbnail"]), C = e.enum([
  "base",
  "enhancement",
  "theme",
  "template"
]), Oe = e.enum(["GOOD", "BETTER", "BEST"]), H = e.enum([
  "all",
  "specific",
  "all_plus_specific",
  "all_except"
]), D = e.object({
  label: e.string().trim().min(1),
  image: e.string().optional(),
  sortOrder: e.int().optional(),
  available: e.boolean().optional(),
  quality: Oe.optional(),
  description: e.string().optional(),
  themeId: t.optional(),
  dependsOn: e.array(
    e.object({
      customizationLabel: e.string(),
      optionLabels: e.array(e.string())
    })
  ).optional()
}), Vt = D.partial(), qt = e.strictObject({
  categoryId: t,
  productId: t.optional(),
  label: e.string().trim().min(1),
  displayType: G,
  customizationType: C.default("base"),
  setDefault: e.boolean().optional(),
  defaultOption: t.optional(),
  available: e.boolean().default(!0),
  sortOrder: e.int().min(0).optional(),
  themeMode: H.optional(),
  options: e.array(D).optional()
}).refine(
  (o) => !(o.setDefault === !0 && !o.defaultOption),
  {
    path: ["defaultOption"],
    error: "defaultOption is required when setDefault is true"
  }
).refine(
  (o) => {
    if (o.defaultOption)
      try {
        return t.parse(o.defaultOption), !0;
      } catch {
        return !1;
      }
    return !0;
  },
  {
    path: ["defaultOption"],
    error: "defaultOption must be a valid UUID"
  }
).refine(
  (o) => {
    if (o.options && o.options.length > 0) {
      const n = o.options.map((l) => l.label.toLowerCase()), i = new Set(n);
      return n.length === i.size;
    }
    return !0;
  },
  {
    path: ["options"],
    error: "Option labels must be unique (case-insensitive)"
  }
), Qt = e.strictObject({
  categoryId: t.optional(),
  productId: t.optional(),
  label: e.string().trim().min(1).optional(),
  displayType: G.optional(),
  customizationType: C.optional(),
  setDefault: e.boolean().optional(),
  defaultOption: t.optional(),
  available: e.boolean().optional(),
  sortOrder: e.int().min(0).optional(),
  themeMode: H.nullable().optional(),
  options: e.array(D).optional(),
  archived: e.boolean().optional()
}).refine(
  (o) => !(o.setDefault === !0 && !o.defaultOption),
  {
    path: ["defaultOption"],
    error: "defaultOption is required when setDefault is true"
  }
).refine(
  (o) => {
    if (o.defaultOption)
      try {
        return t.parse(o.defaultOption), !0;
      } catch {
        return !1;
      }
    return !0;
  },
  {
    path: ["defaultOption"],
    error: "defaultOption must be a valid UUID"
  }
).refine(
  (o) => {
    if (o.options && o.options.length > 0) {
      const n = o.options.map((l) => l.label.toLowerCase()), i = new Set(n);
      return n.length === i.size;
    }
    return !0;
  },
  {
    path: ["options"],
    error: "Option labels must be unique (case-insensitive)"
  }
), Wt = e.object({
  categoryId: t.optional(),
  productId: t.optional(),
  archived: e.boolean().optional(),
  available: e.boolean().optional(),
  customizationType: C.optional()
}), $t = e.object({
  id: t
}), Zt = e.object({
  id: t
}), je = e.enum([
  "paper_stock",
  "cover",
  "binding",
  "envelope"
]), Kt = e.object({
  type: je
}), Pe = e.enum(["PLAIN", "PREPRINTED"]), w = e.object({
  name: e.string().trim().min(1),
  itmRef: e.string().trim().min(1),
  priceCode: e.string().trim().min(1),
  priceTable: e.string().trim().min(1),
  description: e.string().trim().optional(),
  quantityIncrements: e.int().positive().optional(),
  minimumQuantity: e.int().positive().optional(),
  fontColor: e.string().optional(),
  themeId: t.optional(),
  paperWidth: e.number().positive().optional(),
  paperHeight: e.number().positive().optional(),
  // Editor-canvas pixel dimensions. Transient: used only to seed default
  // layout page content, never persisted — theme_width/theme_height columns
  // always mirror paper_width/paper_height (inches), same as MDL.
  widthPixels: e.number().positive().optional(),
  heightPixels: e.number().positive().optional(),
  foldLine: e.string().optional(),
  marginTop: e.int().min(0).optional(),
  marginRight: e.int().min(0).optional(),
  marginBottom: e.int().min(0).optional(),
  marginLeft: e.int().min(0).optional(),
  marginCenterHorizontal: e.int().min(0).optional(),
  marginCenterVertical: e.int().min(0).optional(),
  backgroundType: Pe.optional(),
  identifier: e.record(e.string(), e.string()).optional(),
  hasVariations: e.boolean().optional(),
  available: e.boolean().optional()
}), Xt = w.partial().extend({
  archived: e.boolean().optional()
}), Yt = e.object({
  archived: e.boolean().optional(),
  available: e.boolean().optional(),
  priceCode: e.string().optional(),
  priceTable: e.string().optional(),
  searchTerm: e.string().optional()
}), Jt = e.object({
  id: t
}), eo = e.object({
  itmRef: e.string().min(1)
}), to = e.array(w), Ee = e.object({
  customizationId: t.optional(),
  selectedOptionId: t.optional(),
  themeId: t.optional()
}).refine(
  (o) => {
    const n = !!o.customizationId && !!o.selectedOptionId, i = !!o.themeId, l = (o.customizationId ? 1 : 0) + (o.selectedOptionId ? 1 : 0);
    return n && !i && l === 2 || i && l === 0;
  },
  {
    error: "Selection must be either { customizationId, selectedOptionId } or { themeId }"
  }
), Ce = e.object({
  baseProductId: t,
  name: e.string().trim().min(1),
  itmRef: e.string().trim().min(1),
  priceCode: e.string().trim().min(1),
  priceTable: e.string().trim().min(1),
  description: e.string().trim().optional(),
  selections: e.array(Ee).min(1)
}), oo = Ce.partial().extend({
  archived: e.boolean().optional()
}), no = e.object({
  id: t
}), k = e.object({
  productId: t.optional(),
  productVariationId: t.optional(),
  categoryId: t.optional(),
  themeId: t,
  imageUrl: e.url(),
  sortOrder: e.int().min(0).optional()
}), ao = k.partial().omit({
  productId: !0,
  productVariationId: !0,
  categoryId: !0
}), io = e.object({
  id: t
}), ro = e.object({
  productId: t.optional(),
  productVariationId: t.optional(),
  categoryId: t.optional(),
  themeId: t.optional()
}), lo = e.array(
  k
), V = e.object({
  productId: t,
  quantity: e.int().positive().optional(),
  sortOrder: e.int().min(0).optional()
}), so = e.object({
  funeralHomeId: t,
  name: e.string().trim().min(1, "Name is required"),
  description: e.string().trim().optional(),
  products: e.array(V).optional()
}), mo = e.object({
  name: e.string().trim().min(1, "Name is required").optional(),
  description: e.string().trim().optional(),
  archived: e.boolean().optional(),
  products: e.array(V).optional()
}), co = e.object({
  funeralHomeId: t.optional(),
  archived: e.boolean().optional(),
  searchTerm: e.string().optional()
}), po = e.object({
  id: t
}), uo = e.object({
  bundleId: t,
  productId: t,
  quantity: e.int().positive().optional(),
  sortOrder: e.int().min(0).optional()
}), go = e.object({
  bundleId: t,
  productId: t
}), bo = e.object({
  bundleId: t,
  productId: t,
  quantity: e.int().positive()
}), De = e.object({
  name: e.string().trim().min(1, "Name is required"),
  description: e.string().trim().optional(),
  image: e.string().trim().optional(),
  short_code: e.string().trim().optional(),
  available: e.boolean().optional()
}), ho = e.object({
  name: e.string().trim().min(1, "Name is required").optional(),
  description: e.string().trim().optional(),
  image: e.string().trim().optional().nullable(),
  short_code: e.string().trim().optional(),
  available: e.boolean().optional()
}), Io = e.object({
  available: e.boolean().optional(),
  searchTerm: e.string().optional()
}), fo = e.object({
  id: t
}), So = e.object({
  id: t,
  available: e.boolean()
}), yo = e.array(
  De
), To = e.object({
  applicationType: r,
  orderPageSetId: t
}), Oo = e.object({
  archived: e.boolean().default(!1)
}), jo = e.object({
  pageSetId: e.string().optional(),
  orderPageSetId: e.string().optional(),
  homeTemplate: e.string().optional(),
  default: e.boolean().optional(),
  json_template: e.string().optional(),
  archived: e.boolean().default(!1)
}), Po = e.object({
  applicationType: r,
  orderPageSetId: t
}), Eo = e.object({
  id: t
}), Co = e.object({
  caseId: t.optional(),
  themeId: t.nullable().optional(),
  archived: e.coerce.boolean().optional().default(!1),
  language: e.enum(a).optional()
}), Do = e.object({
  caseId: t.optional(),
  themeId: t.nullable().optional(),
  archived: e.coerce.boolean().optional().default(!1),
  language: e.enum(a).optional(),
  m3OrderProductId: t.optional()
}), Ao = e.object({
  applicationType: r.optional(),
  orderId: t.optional(),
  caseId: t.optional()
}).refine((o) => !!(o.orderId || o.caseId), {
  path: ["orderId"],
  error: "Either orderId or caseId is required"
}), _o = e.object({
  orderId: t.optional()
}), Lo = e.object({
  applicationType: r,
  name: e.string().trim().optional(),
  selected: e.coerce.boolean().optional(),
  archived: e.coerce.boolean().optional(),
  status: e.enum(Y).optional(),
  themeId: t.nullable().optional()
}), vo = e.object({
  applicationType: e.enum(["M3", "MDL"]).optional(),
  funeralHomeId: t.optional(),
  name: e.string().trim().optional(),
  productId: t.optional(),
  m3ProductId: t.optional(),
  orderProductId: t.optional(),
  id: t.optional(),
  language: e.enum(a).optional()
}), Ro = e.object({
  applicationType: r,
  gridStartPointTop: e.number().optional(),
  gridStartPointLeft: e.number().optional(),
  pageNumber: e.number().optional(),
  columns: e.number().optional(),
  rows: e.number().optional(),
  content: e.string().optional(),
  jsonTemplate: e.string().optional(),
  /**
   * Why the client is writing this page, recorded as the reason of the
   * version snapshot the write takes. Absent means a person saved the page
   * (`page_save`); `watermark_sync` is the customize page writing the theme's
   * watermarks onto page 1 with nobody editing. The values mirror
   * SNAPSHOT_REASONS in @messenger/utils.
   */
  saveReason: e.enum(["page_save", "watermark_sync"]).optional(),
  /**
   * The version the client already took for the Save this write belongs to.
   * When that version holds this page as it stands, the write takes no
   * version of its own and the whole Save is covered by one. Absent — from an
   * older client, or a write that is not part of a Save — leaves every write
   * taking its own.
   */
  coveringVersionNumber: e.int().positive().optional()
}), u = e.string().trim().max(50).nullable().transform((o) => o || null).optional(), Uo = e.object({
  name: e.string().trim(),
  productId: t.optional(),
  m3ProductId: t.optional(),
  orderId: t,
  // Left out, a line is not selected.
  selected: e.coerce.boolean().default(!1),
  themeId: t.optional(),
  // Per-line language override. null/omitted = inherit the order language.
  language: e.enum(a).nullable().optional(),
  nickname: u
}), No = e.object({
  caseId: t,
  themeId: t.optional(),
  language: e.enum(a).optional().default(a.EN)
}), Fo = e.object({
  caseId: t.optional(),
  themeId: t.nullable().optional(),
  status: e.string().optional(),
  language: e.enum(a).optional()
}), Mo = e.object({
  name: e.string().trim().optional(),
  available: e.coerce.boolean().optional(),
  default: e.coerce.boolean().optional(),
  productId: t.optional(),
  m3ProductId: t.optional(),
  orderProductDetailsId: t.optional()
}), xo = e.object({
  name: e.string().trim().optional(),
  available: e.coerce.boolean().optional(),
  default: e.coerce.boolean().optional(),
  productId: t.optional(),
  m3ProductId: t.optional(),
  orderProductDetailsId: t.optional()
}), zo = e.object({
  applicationType: r,
  orderPageSetId: t,
  gridStartPointTop: e.number(),
  gridStartPointLeft: e.number(),
  rows: e.number(),
  columns: e.number(),
  pageNumber: e.number(),
  jsonTemplate: e.string(),
  sourcePageId: t.optional()
}), Bo = e.object({
  applicationType: r,
  templateId: t,
  templateType: e.enum(["GLOBAL", "FUNERAL_HOME"]),
  pages: e.array(
    e.object({
      gridStartPointTop: e.number(),
      gridStartPointLeft: e.number(),
      rows: e.number(),
      columns: e.number(),
      pageNumber: e.number(),
      jsonTemplate: e.string(),
      sourcePageId: t
    })
  ).min(1)
}), Go = e.object({
  applicationType: r,
  name: e.string().trim(),
  productId: t.optional(),
  m3ProductId: t.optional(),
  orderProductId: t,
  pageSetId: t,
  homeTemplate: e.boolean().optional(),
  default: e.boolean().optional(),
  language: e.enum(a).optional().default(a.EN)
}), Ho = e.object({
  applicationType: r,
  orderId: t
}), wo = e.object({
  applicationType: r,
  name: e.string().trim().optional(),
  productId: t.optional(),
  m3ProductId: t.optional(),
  pageSetId: t.optional(),
  funeralHomeTemplateId: t.optional(),
  globalTemplateId: t.optional(),
  default: e.boolean().optional(),
  language: e.enum(a).optional(),
  type: e.enum(["GLOBAL", "FUNERAL_HOME"]).optional(),
  currentOrderPageSetIdUsed: t.optional()
}), ko = e.object({
  applicationType: r
}), Vo = e.object({
  ids: e.array(t).min(1),
  applicationType: e.enum(["M3", "MDL"]).optional()
}), qo = e.object({
  caseId: t,
  orderId: t
}), Qo = e.object({
  orderId: t,
  language: e.enum(a)
}), Ae = e.object({
  id: e.string().trim().pipe(t).optional(),
  productId: e.string().trim(),
  themeId: e.string().trim().optional(),
  // Per-line language. Language is materialized on every line (theme parity):
  // omitted on create = the order language is written onto the line; `null` on
  // update = reset the line back to the order language. Omit on update to
  // leave the stored value unchanged.
  language: e.enum(a).nullable().optional(),
  // Per-line label shown as "Name (nickname)". `null` (or "") clears it;
  // omit to leave the stored value unchanged.
  nickname: u
}), q = Ae.extend({
  customizations: e.array(P).optional(),
  customizationsByLabel: E.optional()
}), Q = e.object({
  name: e.string().min(1),
  createdBy: e.string().optional().nullable(),
  canInsertImages: e.boolean().optional().nullable(),
  archived: e.boolean().optional().nullable()
}).optional().nullable(), Wo = e.object({
  funeralHome: e.object({
    id: e.string().trim().optional(),
    name: e.string().trim(),
    phone: e.string().nullable().optional(),
    address: e.string().nullable().optional(),
    city: e.string().nullable().optional(),
    state: e.string().nullable().optional(),
    zipCode: e.string().nullable().optional(),
    corporationId: e.string().nullable().optional(),
    active: e.boolean().default(!0),
    ipCity: e.string().nullable().optional(),
    ipCountryCode: e.string().nullable().optional(),
    ipCountryName: e.string().nullable().optional(),
    ipAddress: e.string().nullable().optional(),
    ipLatitude: e.number().nullable().optional(),
    ipLongitude: e.number().nullable().optional(),
    ipRegionCode: e.string().nullable().optional(),
    ipRegionName: e.string().nullable().optional(),
    ipZipCode: e.string().nullable().optional(),
    isServiceAuthenticated: e.boolean().default(!1),
    freeTrialStartedAt: e.date().nullable().optional(),
    serviceUrlSlug: e.string().nullable().optional(),
    stripeCustomerId: e.string().nullable().optional(),
    stripeSubscriptionStatus: e.string().nullable().optional(),
    stripeSubscriptionEndDate: e.date().nullable().optional(),
    address2: e.string().nullable().optional(),
    emailAddress: e.string().nullable().optional(),
    websiteUrl: e.string().nullable().optional()
  }),
  funeralHomeSettings: e.object({
    datesOfLifeFormat: e.string().max(150).optional(),
    datesOfServiceFormat: e.string().max(105).optional(),
    birthHeader: e.string().max(500).optional(),
    deathHeader: e.string().max(500).optional(),
    remembranceHeader: e.string().max(500).optional(),
    language: e.enum(a),
    timePeriod: e.string().max(50).optional(),
    timeRangeSeparator: e.string().max(10).optional(),
    showTimeNoon: e.boolean().optional(),
    removeMinutes: e.boolean().optional()
  }).optional(),
  user: e.object({
    id: e.string().trim().optional(),
    name: e.string().trim(),
    email: e.email(),
    corporationId: e.string().nullable().optional()
  }),
  case: ue,
  // Optional so the middleware can create an empty order and add each line
  // afterwards via POST /api/order/:orderId/product; omitting the key is the
  // same as sending an empty array.
  products: e.union([
    e.string().array(),
    q.array()
  ]).optional().default([]),
  themeId: e.string().trim(),
  corporation: Q,
  language: e.enum(a).optional().default(a.EN)
}), $o = e.object({
  funeralHome: e.object({
    id: e.string().trim().optional(),
    name: e.string().trim(),
    phone: e.string().nullable().optional(),
    address: e.string().nullable().optional(),
    city: e.string().nullable().optional(),
    state: e.string().nullable().optional(),
    zipCode: e.string().nullable().optional(),
    corporationId: e.string().nullable().optional(),
    active: e.boolean().default(!0),
    ipCity: e.string().nullable().optional(),
    ipCountryCode: e.string().nullable().optional(),
    ipCountryName: e.string().nullable().optional(),
    ipAddress: e.string().nullable().optional(),
    ipLatitude: e.number().nullable().optional(),
    ipLongitude: e.number().nullable().optional(),
    ipRegionCode: e.string().nullable().optional(),
    ipRegionName: e.string().nullable().optional(),
    ipZipCode: e.string().nullable().optional(),
    isServiceAuthenticated: e.boolean().default(!1),
    freeTrialStartedAt: e.date().nullable().optional(),
    serviceUrlSlug: e.string().nullable().optional(),
    stripeCustomerId: e.string().nullable().optional(),
    stripeSubscriptionStatus: e.string().nullable().optional(),
    stripeSubscriptionEndDate: e.date().nullable().optional(),
    address2: e.string().nullable().optional(),
    emailAddress: e.string().nullable().optional(),
    websiteUrl: e.string().nullable().optional()
  }).optional().nullable(),
  funeralHomeSettings: e.object({
    datesOfLifeFormat: e.string().max(150).optional(),
    datesOfServiceFormat: e.string().max(105).optional(),
    birthHeader: e.string().max(500).optional(),
    deathHeader: e.string().max(500).optional(),
    remembranceHeader: e.string().max(500).optional(),
    language: e.enum(a),
    timePeriod: e.string().max(50).optional(),
    timeRangeSeparator: e.string().max(10).optional(),
    showTimeNoon: e.boolean().optional(),
    removeMinutes: e.boolean().optional()
  }).optional().nullable(),
  user: e.object({
    id: e.string().trim().optional(),
    name: e.string().trim(),
    email: e.email(),
    corporationId: e.string().nullable().optional()
  }).optional().nullable(),
  corporation: Q
}), Zo = e.object({
  products: e.union([
    e.string().array(),
    q.array()
  ]).optional(),
  themeId: e.string().trim().optional(),
  resetProductLayoutIds: e.union([e.array(e.string()), e.boolean()]).optional(),
  language: e.enum(a).optional(),
  funeralHome: e.object({
    id: e.string().trim().optional(),
    name: e.string().trim().optional(),
    phone: e.string().nullable().optional(),
    address: e.string().nullable().optional(),
    city: e.string().nullable().optional(),
    state: e.string().nullable().optional(),
    zipCode: e.string().nullable().optional(),
    corporationId: e.string().nullable().optional(),
    active: e.boolean().optional(),
    ipCity: e.string().nullable().optional(),
    ipCountryCode: e.string().nullable().optional(),
    ipCountryName: e.string().nullable().optional(),
    ipAddress: e.string().nullable().optional(),
    ipLatitude: e.number().nullable().optional(),
    ipLongitude: e.number().nullable().optional(),
    ipRegionCode: e.string().nullable().optional(),
    ipRegionName: e.string().nullable().optional(),
    ipZipCode: e.string().nullable().optional(),
    isServiceAuthenticated: e.boolean().optional(),
    freeTrialStartedAt: e.date().nullable().optional(),
    serviceUrlSlug: e.string().nullable().optional(),
    stripeCustomerId: e.string().nullable().optional(),
    stripeSubscriptionStatus: e.string().nullable().optional(),
    stripeSubscriptionEndDate: e.date().nullable().optional(),
    address2: e.string().nullable().optional(),
    emailAddress: e.string().nullable().optional(),
    websiteUrl: e.string().nullable().optional()
  }).optional()
}), Ko = e.object({
  productId: e.string().trim().pipe(t),
  themeId: e.string().trim().pipe(t).optional(),
  // Per-line language override. Omit to inherit the order-level language.
  language: e.enum(a).optional(),
  // Per-line label shown as "Name (nickname)". Omit for no label.
  nickname: u,
  customizations: e.array(P).optional(),
  customizationsByLabel: E.optional()
}), Xo = e.object({
  themeId: e.string().trim().pipe(t).optional(),
  // Per-line language override. An explicit `null` clears the override so
  // the line falls back to the order-level language; omit to leave as-is.
  language: e.enum(a).nullable().optional(),
  selected: e.boolean().optional(),
  // Per-line label shown as "Name (nickname)". `null` (or "") clears it;
  // omit to leave the stored value unchanged.
  nickname: u,
  customizations: e.array(P).optional(),
  customizationsByLabel: E.optional()
}).refine(
  (o) => o.themeId !== void 0 || o.language !== void 0 || o.selected !== void 0 || o.nickname !== void 0 || o.customizations !== void 0 || o.customizationsByLabel !== void 0,
  {
    error: "Provide at least one of: themeId, language, selected, nickname, customizations, customizationsByLabel"
  }
), Yo = e.object({
  actAsAdmin: e.boolean()
}), Jo = e.object({
  locationGuid: s("Invalid location GUID format")
}), en = e.object({
  caseId: e.string().regex(/^\d+$/, "Case ID must be a number").transform(Number)
}), tn = e.object({
  funeralHomeId: e.string().regex(/^\d+$/, "Funeral Home ID must be a number").transform(Number)
}), on = e.object({
  pattern: e.string().min(1, "Cache pattern is required")
}), nn = e.object({
  guid: s("Invalid location GUID format"),
  id: e.string().regex(/^\d+$/, "Case ID must be a number").transform(Number)
}), an = e.object({
  guid: s("Invalid location GUID format"),
  caseId: e.string().regex(/^\d+$/, "Case ID must be a number").transform(Number)
}), rn = e.object({
  guid: s("Invalid location GUID format"),
  id: e.string().regex(/^\d+$/, "Case ID must be a number").transform(Number)
}), ln = e.object({
  guid: s("Invalid location GUID format")
}), sn = e.object({
  dateDied: e.string().min(1, "dateDied parameter is required"),
  lastName: e.string().min(1, "lastName parameter is required")
}), mn = e.object({
  applicationType: r,
  id: t.optional(),
  name: e.string().trim().optional(),
  productId: t.optional(),
  m3ProductId: t.optional(),
  themeId: t.optional(),
  customPages: e.coerce.boolean().optional(),
  homeTemplate: e.coerce.boolean().optional(),
  default: e.coerce.boolean().optional(),
  orderId: t.optional(),
  language: e.enum(a).optional()
}), cn = e.object({
  applicationType: r,
  id: t.optional(),
  name: e.string().trim().optional(),
  productId: t.optional(),
  m3ProductId: t.optional(),
  themeId: t.optional(),
  customPages: e.coerce.boolean().optional(),
  homeTemplate: e.coerce.boolean().optional(),
  default: e.coerce.boolean().optional(),
  orderId: t.optional(),
  language: e.enum(a).optional()
}), pn = e.object({
  name: e.string().trim(),
  productId: t.optional(),
  m3ProductId: t.optional(),
  themeId: t,
  homeTemplate: e.boolean(),
  default: e.coerce.boolean().default(!1),
  language: e.enum(a).default(a.EN),
  applicationType: r
}), un = e.object({
  name: e.string().trim(),
  productId: t.optional(),
  m3ProductId: t.optional(),
  themeId: t,
  homeTemplate: e.coerce.boolean().default(!1),
  default: e.coerce.boolean().default(!1),
  language: e.enum(a).default(a.EN)
}), dn = e.object({
  pageSetId: t,
  gridStartPointTop: e.number(),
  gridStartPointLeft: e.number(),
  rows: e.number(),
  columns: e.number(),
  pageNumber: e.number(),
  jsonTemplate: e.string(),
  sourceOrderPageId: t.optional()
}), W = e.object({
  gridStartPointTop: e.number(),
  gridStartPointLeft: e.number(),
  rows: e.number(),
  columns: e.number(),
  pageNumber: e.number(),
  jsonTemplate: e.string(),
  sourceOrderPageId: t
}), gn = e.object({
  name: e.string().trim(),
  default: e.boolean(),
  language: e.enum(a),
  homeTemplate: e.boolean(),
  pages: e.array(W).min(1)
}), bn = e.object({
  name: e.string().trim(),
  productId: t.optional(),
  themeId: t,
  default: e.boolean(),
  language: e.enum(a).default(a.EN),
  pages: e.array(W).min(1)
}), hn = e.object({
  pageSetId: t.optional(),
  gridStartPointTop: e.number().optional(),
  gridStartPointLeft: e.number().optional(),
  rows: e.number().optional(),
  columns: e.number().optional(),
  pageNumber: e.number().optional(),
  template: e.string().optional(),
  jsonTemplate: e.string().optional(),
  archived: e.coerce.boolean().optional()
}), In = e.object({
  archived: e.coerce.boolean().optional()
}), fn = e.object({
  pageSetId: t
}), Sn = e.object({
  name: e.string().trim().optional(),
  productId: e.string().optional(),
  m3ProductId: e.string().optional(),
  themeId: e.string().optional(),
  funeralHomeId: e.string().optional(),
  homeTemplate: e.coerce.boolean().optional(),
  default: e.coerce.boolean().optional(),
  language: e.enum(a).optional()
}), _e = 'may hold only letters, digits, "_" and "-"', Le = e.object({
  itemId: e.string(),
  kind: e.enum(["tinymce", "lotus"]),
  pageIndex: e.number(),
  pageId: e.string().nullish(),
  settled: e.boolean(),
  frame: e.object({
    x: e.number(),
    y: e.number(),
    width: e.number(),
    height: e.number()
  }),
  fontSizesPt: e.array(e.number()).nullish(),
  scale: e.number().nullish(),
  lines: e.number().nullish(),
  loadHeight: e.number().nullish(),
  ceiling: e.number().nullish(),
  boundedBy: e.string().nullish(),
  overflowing: e.boolean().nullish(),
  /**
   * The room a flagged frame is past. The request loses its nulls on the way
   * (the API's denormalizer drops them), so a bound or a line height the
   * surface recorded as null arrives absent: both are read as nullish and
   * handed on as null, the shape the digest compares.
   */
  kept: e.object({
    ceiling: e.number(),
    boundedBy: e.string().nullish(),
    lineHeight: e.number().nullish()
  }).transform(({ ceiling: o, boundedBy: n, lineHeight: i }) => ({
    ceiling: o,
    boundedBy: n ?? null,
    lineHeight: i ?? null
  })).nullish(),
  sizing: e.string().nullish(),
  maxFontSizePt: e.number().nullish(),
  passes: e.number().nullish(),
  textHash: e.string().nullish(),
  stamps: e.record(
    e.string(),
    e.union([e.string(), e.number(), e.boolean(), e.null()])
  ).nullish()
}), ve = e.object({
  surface: e.string(),
  takenAt: e.number().nullish(),
  boxes: e.array(Le)
}), yn = e.object({
  html: e.string().trim(),
  pageHeight: e.coerce.number(),
  pageWidth: e.coerce.number(),
  orderId: e.string().trim(),
  productId: e.string().trim(),
  orderPageSetId: e.string().trim(),
  sendToWorker: e.boolean().optional(),
  frontendDomain: e.string().trim().optional(),
  /**
   * The server derives the job's funeral home from the order row and ignores
   * this field's value. The field is still required (an empty string passes),
   * and its pattern keeps a malformed value from passing the schema.
   */
  funeralHomeId: e.string().trim().regex(ae, _e),
  applicationType: r,
  /**
   * The requesting surface's text geometry, so the worker can hold its own
   * paint against it box by box. Optional: a request without one is
   * rendered as before and compared against nothing.
   */
  textGeometry: ve.optional()
}), Tn = e.object({
  name: e.string().trim().optional()
}), On = e.object({
  productId: t,
  isM3: e.boolean().optional().default(!1)
}), Re = 25, Ue = 25, jn = 3, Pn = 15, Ne = 2e3, En = e.object({
  orderId: e.string().min(1),
  // 'MDL' orders share through the MDL flow; 'M3' orders (opened in the MDL
  // editor via an edit token) are forwarded to the M3 internal proof-share API.
  applicationType: r,
  orderProductIds: e.array(e.string()).min(1).max(Ue),
  recipientEmails: e.array(e.string().trim().pipe(e.email())).min(1).max(Re),
  // Reply-To on the share email. Optional: the server falls back to the
  // sharing user's email when omitted.
  replyToEmail: e.string().trim().pipe(e.email()).optional(),
  // Optional free-text message from the director, rendered in the share email.
  comment: e.string().trim().max(Ne).optional()
}), d = e.enum([
  "DEFAULT",
  "VISITATION_SERVICE",
  "VISITATION_ALL",
  "SERVICES",
  "ORDER_OF_SERVICE",
  "PALLBEARERS"
]), Cn = d.options, Dn = e.object({
  productId: t,
  category: d.optional()
}), An = e.object({
  template: e.string().trim(),
  name: e.string().trim().nonempty(),
  productId: t,
  themeId: t.optional().nullable(),
  category: d.optional()
}), _n = e.object({
  template: e.string().trim().optional(),
  name: e.string().trim().nonempty().optional(),
  category: d.optional()
}), Ln = e.object({
  applicationType: r,
  layoutId: t.optional(),
  productId: t.optional(),
  themeId: t.optional(),
  archived: e.coerce.boolean().default(!1)
}), vn = e.object({
  //customThemes: z.coerce.boolean().optional().default(false),
  archived: e.coerce.boolean().optional().default(!1)
}), Rn = e.object({
  name: e.string().trim(),
  description: e.string().trim().optional(),
  image: e.string().trim().optional(),
  font: e.string().trim().transform(ne).optional(),
  fontColorAccent1: e.string().trim().optional(),
  fontColorAccent2: e.string().trim().optional(),
  fontColor: e.string().trim().regex(/#\w+/, {
    error: "Invalid hex color"
  }).optional()
});
export {
  r as ApplicationTypeSchema,
  xe as EDITOR_TEXT_BOX_ITEM_TYPE,
  _e as FUNERAL_HOME_ID_SEGMENT_MESSAGE,
  Te as LOTUS_TURNOVER_TRANSACTION_BUDGET,
  Ne as MAX_PROOF_SHARE_COMMENT_LENGTH,
  Ue as MAX_PROOF_SHARE_PRODUCTS,
  Re as MAX_PROOF_SHARE_RECIPIENTS,
  jn as PROOF_SHARE_EXPIRATION_DAYS,
  Pn as PROOF_SHARE_HOLD_TIMEOUT_MINUTES,
  He as REDIS_CHANNELS,
  se as RETURN_URL_MAX_LENGTH,
  Cn as SERVICE_TEMPLATE_CATEGORY_ORDER,
  b as UPSCALE_TARGET_EDGE_OUT_OF_RANGE_MESSAGE,
  de as UPSCALE_TARGET_KIND_MESSAGE,
  Se as addCustomIssue,
  uo as addProductToBundleSchema,
  Bo as applyOrderPageSetLayoutSchema,
  In as archiveOrderPageSchema,
  Pe as backgroundTypeSchema,
  ut as batchJobStatusInputSchema,
  xt as bulkCreateCustomizationOptionsSchema,
  on as cachePatternSchema,
  it as caseCreateAdditionalData,
  ue as caseCreateSchema,
  en as caseIdSchema,
  Ct as caseImageVariantAvailabilitySchema,
  be as caseImageVariantForCaseListItemSchema,
  x as caseImageVariantListItemSchema,
  Et as caseImageVariantPricingSchema,
  yt as caseImageVariantRefusedResponseSchema,
  Pt as caseImageVariantRevertedResponseSchema,
  Ot as caseImageVariantsForCaseResponseSchema,
  Tt as caseImageVariantsForImageResponseSchema,
  Dt as caseMemorialsSchema,
  nt as casePreferencesSchema,
  O as casePreferencesUpdateSchema,
  at as caseUpdateSchema,
  ft as checkAllFamilyImagesInUseInputSchema,
  It as checkFamilyImageInUseInputSchema,
  Ve as copyAllProductTemplatesSchema,
  Q as corporationCreateSchema,
  Ze as createAssetSubcategoriesSchema,
  Xe as createAssetsSchema,
  St as createCaseImageVariantInputSchema,
  At as createCaseMemorialSchema,
  Rn as createCustomThemeSchema,
  _t as createCustomVerseSchema,
  Qe as createDefaultLayoutsForProductSchema,
  An as createGlobalServiceInformationTemplateSchema,
  $o as createMetadataSchema,
  qe as createNewProductSchema,
  zo as createOrderPageDetailsSchema,
  Go as createOrderPageSetSchema,
  Uo as createOrderProductsSchema,
  No as createOrderSchema,
  dn as createPageSchema,
  pn as createPageSetSchema,
  un as createPageSetSchemaV2,
  En as createProofShareRequestSchema,
  Wo as createSessionSchema,
  bn as createTemplateWithPagesSchema,
  et as createVerseFormatSchema,
  P as customizationByIdSelectionSchema,
  Kt as customizationCompareDetailsSchema,
  je as customizationCompareTypeSchema,
  Rt as customizationCreateSchema,
  G as customizationDisplayTypeSchema,
  Nt as customizationFiltersSchema,
  Ft as customizationIdSchema,
  p as customizationOptionCreateSchema,
  Mt as customizationOptionIdSchema,
  vt as customizationOptionUpdateSchema,
  Oe as customizationQualitySchema,
  C as customizationTypeSchema,
  Ut as customizationUpdateSchema,
  E as customizationsByLabelSchema,
  ht as deleteAllFamilyImagesInputSchema,
  bt as deleteFamilyImageInputSchema,
  st as deleteImageInputSchema,
  Vo as deleteOrderPagesBulkSchema,
  Bt as designRequestQueueItemParamsSchema,
  zt as designRequestQueueListQuerySchema,
  qo as duplicateOrderSchema,
  We as duplicateThemeSchema,
  $ as editorBackButtonPayloadSchema,
  Be as editorBackButtonRequestSchema,
  Ge as editorBackButtonResponseSchema,
  Ho as ensureOrderPageSetsSchema,
  dt as familyPresignedUploadInputSchema,
  gt as familyProcessImageInputSchema,
  tn as funeralHomeIdSchema,
  yn as generatePDFSchema,
  an as getAllExpressionGuestsSchema,
  sn as getAllFuneralHomeCasesQuerySchema,
  ln as getAllFuneralHomeCasesSchema,
  $e as getAssetSubcategoriesSchema,
  ie as getAssetsSchema,
  rn as getCaseProfileImagesSchema,
  nn as getCaseProfileSchema,
  ot as getCasesSchema,
  To as getEditorOrderPageDetailsSchema,
  Oo as getFuneralHomeOrdersSchema,
  Dn as getGlobalServiceInformationTemplatesQuerySchema,
  Me as getMeResponseSchema,
  xo as getOrderLayoutDetailsSchema,
  Po as getOrderPageDetailSchema,
  Ao as getOrderProductsSchema,
  Do as getOrderSchema,
  On as getProductLayoutsSchema,
  Je as getVerseFormatSchema,
  Lt as getVersesQuerySchema,
  pe as isAllowedReturnUrl,
  Ht as isZodError,
  ke as layoutTemplateCopySchema,
  Jo as locationGuidSchema,
  wt as lotusTurnoverBodySchema,
  ye as lotusTurnoverPageSchema,
  kt as lotusTurnoverWorstCaseMs,
  qt as m3CustomizationCreateSchema,
  Wt as m3CustomizationFiltersSchema,
  $t as m3CustomizationIdSchema,
  D as m3CustomizationOptionCreateSchema,
  Zt as m3CustomizationOptionIdSchema,
  Vt as m3CustomizationOptionUpdateSchema,
  Qt as m3CustomizationUpdateSchema,
  to as m3ProductBulkCreateSchema,
  w as m3ProductCreateSchema,
  Yt as m3ProductFiltersSchema,
  Jt as m3ProductIdSchema,
  lo as m3ProductImageBulkCreateSchema,
  k as m3ProductImageCreateSchema,
  ro as m3ProductImageFiltersSchema,
  io as m3ProductImageIdSchema,
  ao as m3ProductImageUpdateSchema,
  eo as m3ProductItmRefSchema,
  Xt as m3ProductUpdateSchema,
  Ce as m3ProductVariationCreateSchema,
  no as m3ProductVariationIdSchema,
  Ee as m3ProductVariationSelectionInputSchema,
  oo as m3ProductVariationUpdateSchema,
  z as mdlCustomizationDisplayTypeSchema,
  j as mdlCustomizationTypeSchema,
  ze as mdlEditorEventSchema,
  B as mdlThemeModeSchema,
  we as migrateDataSchema,
  mt as moveToFrontInputSchema,
  F as nameArrangementSchema,
  Mo as orderLayoutDetailsSchema,
  jo as orderPageDetailSchema,
  Eo as orderPageDetailSchemaV2,
  vo as orderPageSetSchema,
  Ro as orderPageUpdateSchema,
  u as orderProductNicknameSchema,
  _o as orderProductsSchemaV2,
  Lo as orderProductsUpdateSchema,
  Co as orderSchema,
  mn as pageSetSchema,
  cn as pageSetSchemaV2,
  fn as pagesSchema,
  ct as presignedUploadBatchInputSchema,
  rt as presignedUploadInputSchema,
  pt as processBatchInputSchema,
  lt as processImageInputSchema,
  so as productBundleCreateSchema,
  co as productBundleFiltersSchema,
  po as productBundleIdSchema,
  V as productBundleItemSchema,
  mo as productBundleUpdateSchema,
  yo as productCategoryBulkCreateSchema,
  De as productCategoryCreateSchema,
  Io as productCategoryFiltersSchema,
  fo as productCategoryIdSchema,
  So as productCategoryToggleAvailabilitySchema,
  ho as productCategoryUpdateSchema,
  q as productWithThemeAndCustomizationsSchema,
  Ae as productWithThemeSchema,
  Tn as productsSchema,
  go as removeProductFromBundleSchema,
  gn as replaceTemplatePagesSchema,
  ko as resetOrderProductSchema,
  M as returnUrlSchema,
  t as rowIdSchema,
  s as rowIdSchemaWithMessage,
  d as serviceTemplateCategorySchema,
  jt as setCaseImageVariantRevertedInputSchema,
  Ko as singleOrderProductCreateSchema,
  Xo as singleOrderProductUpdateSchema,
  W as templatePageBodySchema,
  Le as textGeometryDigestBoxSchema,
  ve as textGeometryDigestSchema,
  H as themeModeSchema,
  Ln as themeOverridesSchema,
  vn as themesSchema,
  Ke as updateAssetSubcategoriesSchema,
  Ye as updateAssetsSchema,
  bo as updateBundleItemQuantitySchema,
  _n as updateGlobalServiceInformationTemplateSchema,
  Zo as updateMessengerOrderSchema,
  wo as updateOrderPageSetSchema,
  Fo as updateOrderSchema,
  hn as updatePageSchema,
  Sn as updatePageSetSchemaV2,
  Yo as updateUserActAsAdminSchema,
  tt as updateVerseFormatSchema,
  ge as upscaleTargetSizeSchema,
  Qo as validateOrderLanguageSchema,
  Gt as watermarksSchema
};
